/* eslint-disable @typescript-eslint/no-explicit-any */
import api from '../../services/axios'
import {
  ValidacaoSelloutParams,
  ValidacaoSelloutResponse,
  FamiliaSelloutItem,
  FamiliaEstoqueItem,
  FamiliaCoberturaItem,
  AcompanhamentoDiarioParams,
  AcompanhamentoDiarioResponse,
} from './types'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

/**
 * 1. Rota: Buscar Cidades Disponíveis
 * POST /sellout/validacao/cidades
 * Payload: { distIds: number[] }
 */
export const buscarCidadesPorDistribuidores = async (
  distribuidorIds: (string | number)[],
): Promise<string[]> => {
  try {
    const distIds = (distribuidorIds || [])
      .map((id) => {
        const n = Number(id)
        return isNaN(n) ? id : n
      })

    const res = await api.post('/sellout/validacao/cidades', { distIds })
    const list = res.data?.data ?? res.data ?? []
    return Array.isArray(list) ? list : []
  } catch (err) {
    console.error('[ValidacaoSellout] Erro ao buscar cidades:', err)
    return []
  }
}

/**
 * 2. Rota: Validação de Sellout
 * POST /sellout/validacao
 * Payload conforme documentação atualizada:
 * {
 *   distIds: number[],
 *   ano: number,
 *   mes: string,
 *   semana: string,
 *   cidades: string[],
 *   familias: string[]
 * }
 */
export const fetchValidacaoSellout = async (
  params: ValidacaoSelloutParams,
): Promise<ValidacaoSelloutResponse> => {
  try {
    const distIds = (params.distribuidorIds || []).map((id) => {
      const n = Number(id)
      return isNaN(n) ? id : n
    })

    // Formata semana conforme especificação da API:
    // Suporta "Mês Completo", semana única ("Semana 1") ou múltiplas semanas (["Semana 1", "Semana 2"])
    const isYtd = params.tipoPeriodo === 'ytd'
    const sortedSemanas = (params.semanas || []).slice().sort((a, b) => a - b)
    const isMesCompleto =
      params.mesCompleto ||
      sortedSemanas.length === 0 ||
      sortedSemanas.length === 4

    let semanaStr = 'Mês Completo'
    let semanasPayload: string[] = []

    if (isYtd) {
      semanaStr = 'YTD (Acumulado)'
      semanasPayload = []
    } else if (isMesCompleto) {
      semanaStr = 'Mês Completo'
      semanasPayload = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4']
    } else if (sortedSemanas.length === 1) {
      semanaStr = `Semana ${sortedSemanas[0]}`
      semanasPayload = [`Semana ${sortedSemanas[0]}`]
    } else {
      semanaStr = sortedSemanas.map((s) => `Semana ${s}`).join(', ')
      semanasPayload = sortedSemanas.map((s) => `Semana ${s}`)
    }

    const payload: any = {
      distIds,
      ano: Number(params.ano),
      mes: MONTH_NAMES[params.mes] || 'Agosto',
      semana: semanaStr,
      semanas: semanasPayload,
      tipoPeriodo: isYtd ? 'ytd' : 'mensal',
    }

    if (isYtd) {
      payload.isYtd = true
      payload.ytdModo = params.ytdModo || 'tudo'
      if (params.mesesYtd && params.mesesYtd.length > 0) {
        // Envia meses convertendo de 0-11 para 1-12
        payload.mesesYtd = params.mesesYtd.map((m) => m + 1)
      }
    }

    if (params.cidades && params.cidades.length > 0) {
      payload.cidades = params.cidades
    }

    console.log('[ValidacaoSellout] Enviando payload para /sellout/validacao:', payload)

    const res = await api.post('/sellout/validacao', payload)
    const apiData = res.data?.data ?? res.data ?? {}

    const resumo = apiData.resumo || {}
    const categorias = Array.isArray(apiData.categorias) ? apiData.categorias : []
    const estoque = Array.isArray(apiData.estoque) ? apiData.estoque : []
    const cobertura = Array.isArray(apiData.cobertura) ? apiData.cobertura : []
    const filtrosAplicados = apiData.filtrosAplicados || undefined

    // 1. Mapeamento Categorias (Sellout)
    const selloutPorFamilia: FamiliaSelloutItem[] = categorias.map((item: any) => {
      const valorRS = Number(item.valor ?? item.valorRS ?? 0)
      const caixas = Number(item.caixas ?? 0)

      // Metas (Novo)
      const meta_caixas = typeof item.meta_caixas === 'number' ? item.meta_caixas : undefined
      const meta_valor = typeof item.meta_valor === 'number' ? item.meta_valor : undefined
      const atingimento_caixas_percentual =
        typeof item.atingimento_caixas_percentual === 'number'
          ? item.atingimento_caixas_percentual
          : undefined
      const atingimento_valor_percentual =
        typeof item.atingimento_valor_percentual === 'number'
          ? item.atingimento_valor_percentual
          : undefined
      const variacao_meta_cx =
        typeof item.variacao_meta_cx === 'number' ? item.variacao_meta_cx : undefined
      const variacao_meta_percentual =
        typeof item.variacao_meta_percentual === 'number'
          ? item.variacao_meta_percentual
          : meta_caixas && meta_caixas > 0
            ? Number((((caixas - meta_caixas) / meta_caixas) * 100).toFixed(1))
            : undefined
      const tendencia_meta = item.tendencia_meta
      const variacao_meta_valor =
        typeof item.variacao_meta_valor === 'number' ? item.variacao_meta_valor : undefined
      const variacao_meta_valor_percentual =
        typeof item.variacao_meta_valor_percentual === 'number'
          ? item.variacao_meta_valor_percentual
          : meta_valor && meta_valor > 0
            ? Number((((valorRS - meta_valor) / meta_valor) * 100).toFixed(1))
            : undefined
      const tendencia_meta_valor = item.tendencia_meta_valor

      // Médias 3M (Para o modal de comparação)
      const media_3m_caixas =
        typeof item.media_3m_caixas === 'number' ? item.media_3m_caixas : undefined
      const variacao_cx =
        typeof item.variacao_cx === 'number' ? item.variacao_cx : undefined

      const variacao_percentual =
        typeof item.variacao_percentual === 'number'
          ? item.variacao_percentual
          : media_3m_caixas && media_3m_caixas > 0
            ? Number((((caixas - media_3m_caixas) / media_3m_caixas) * 100).toFixed(1))
            : undefined

      const media_3m_valor =
        typeof item.media_3m_valor === 'number'
          ? item.media_3m_valor
          : typeof item.media_3m_rs === 'number'
            ? item.media_3m_rs
            : undefined

      const variacao_valor_percentual =
        typeof item.variacao_valor_percentual === 'number'
          ? item.variacao_valor_percentual
          : typeof item.variacao_percentual_valor === 'number'
            ? item.variacao_percentual_valor
            : media_3m_valor && media_3m_valor > 0
              ? Number((((valorRS - media_3m_valor) / media_3m_valor) * 100).toFixed(1))
              : undefined

      return {
        familia: item.familia || '',
        valorRS,
        caixas,
        sharePercent: Number(item.percentual ?? item.sharePercent ?? 0),

        // Metas
        meta_caixas,
        meta_valor,
        atingimento_caixas_percentual,
        atingimento_valor_percentual,
        variacao_meta_cx,
        variacao_meta_percentual,
        tendencia_meta,
        variacao_meta_valor,
        variacao_meta_valor_percentual,
        tendencia_meta_valor,

        // Médias 3M
        media_3m_caixas,
        variacao_cx,
        variacao_percentual,
        tendencia: item.tendencia,
        media_3m_valor,
        variacao_valor:
          typeof item.variacao_valor === 'number' ? item.variacao_valor : undefined,
        variacao_valor_percentual,
        tendencia_valor: item.tendencia_valor,
        skus: Array.isArray(item.skus)
          ? item.skus.map((s: any) => ({
              sku: String(s.sku || ''),
              descricao: String(s.descricao || s.sku || ''),
              valorRS: Number(s.valor ?? s.valorRS ?? 0),
              caixas: Number(s.caixas ?? 0),
              sharePercent: Number(s.share_percent ?? s.sharePercent ?? 0),
              shareTotalPercent: typeof s.share_total_percent === 'number' ? s.share_total_percent : undefined,
              clientesPositivados: typeof s.clientes === 'number' ? s.clientes : undefined,
              media_3m_caixas: typeof s.media_3m_caixas === 'number' ? s.media_3m_caixas : undefined,
              variacao_cx: typeof s.variacao_cx === 'number' ? s.variacao_cx : undefined,
              variacao_percentual: typeof s.variacao_percentual === 'number' ? s.variacao_percentual : undefined,
              tendencia: s.tendencia,
              media_3m_valor: typeof s.media_3m_valor === 'number' ? s.media_3m_valor : undefined,
              variacao_valor: typeof s.variacao_valor === 'number' ? s.variacao_valor : undefined,
              variacao_valor_percentual: typeof s.variacao_valor_percentual === 'number' ? s.variacao_valor_percentual : undefined,
              tendencia_valor: s.tendencia_valor,
            }))
          : [],
      }
    })

    // 2. Mapeamento Estoque
    const estoquePorFamilia: FamiliaEstoqueItem[] = estoque.map((item: any) => ({
      familia: item.familia || '',
      caixas: Number(item.caixas ?? 0),
      valorRS: Number(item.valor ?? item.valorRS ?? 0),
      media_3m_caixas: typeof item.media_3m_caixas === 'number' ? item.media_3m_caixas : undefined,
      variacao_cx: typeof item.variacao_cx === 'number' ? item.variacao_cx : undefined,
      variacao_percentual: typeof item.variacao_percentual === 'number' ? item.variacao_percentual : undefined,
      tendencia: item.tendencia,
      skus: Array.isArray(item.skus)
        ? item.skus.map((s: any) => ({
            sku: String(s.sku || ''),
            descricao: String(s.descricao || s.sku || ''),
            caixas: Number(s.caixas ?? 0),
            valorRS: Number(s.valor ?? s.valorRS ?? 0),
            unidades: typeof s.unidades === 'number' ? s.unidades : undefined,
            media_3m_caixas: typeof s.media_3m_caixas === 'number' ? s.media_3m_caixas : undefined,
            variacao_cx: typeof s.variacao_cx === 'number' ? s.variacao_cx : undefined,
            variacao_percentual: typeof s.variacao_percentual === 'number' ? s.variacao_percentual : undefined,
            tendencia: s.tendencia,
            media_3m_valor: typeof s.media_3m_valor === 'number' ? s.media_3m_valor : undefined,
            variacao_valor: typeof s.variacao_valor === 'number' ? s.variacao_valor : undefined,
            variacao_valor_percentual: typeof s.variacao_valor_percentual === 'number' ? s.variacao_valor_percentual : undefined,
            tendencia_valor: s.tendencia_valor,
          }))
        : [],
    }))

    // 3. Mapeamento Cobertura (com potencial e gap territorial)
    const coberturaPorFamilia: FamiliaCoberturaItem[] = cobertura.map((item: any) => {
      const clientesPositivados = Number(item.clientes ?? item.clientesPositivados ?? 0)

      return {
        familia: item.familia || '',
        clientesPositivados,
        percentual: typeof item.percentual === 'number' ? item.percentual : undefined,

        // Potencial territorial e gap
        potencial_clientes:
          typeof item.potencial_clientes === 'number' ? item.potencial_clientes : undefined,
        gap_clientes: typeof item.gap_clientes === 'number' ? item.gap_clientes : undefined,
        atingimento_potencial_pct:
          typeof item.atingimento_potencial_pct === 'number'
            ? item.atingimento_potencial_pct
            : undefined,

        // Médias 3M
        media_3m_clientes: typeof item.media_3m_clientes === 'number' ? item.media_3m_clientes : undefined,
        variacao_clientes: typeof item.variacao_clientes === 'number' ? item.variacao_clientes : undefined,
        variacao_percentual: typeof item.variacao_percentual === 'number' ? item.variacao_percentual : undefined,
        tendencia: item.tendencia,
        skus: Array.isArray(item.skus)
          ? item.skus.map((s: any) => ({
              sku: String(s.sku || ''),
              descricao: String(s.descricao || s.sku || ''),
              clientesPositivados: Number(s.clientes ?? s.clientesPositivados ?? 0),
              media_3m_clientes: typeof s.media_3m_clientes === 'number' ? s.media_3m_clientes : undefined,
              variacao_clientes: typeof s.variacao_clientes === 'number' ? s.variacao_clientes : undefined,
              variacao_percentual: typeof s.variacao_percentual === 'number' ? s.variacao_percentual : undefined,
              tendencia: s.tendencia,
            }))
          : [],
      }
    })

    // Totais gerais consolidados
    const totalSelloutRS = Number(
      resumo.sellout_total_valor ??
        selloutPorFamilia.reduce((acc, curr) => acc + curr.valorRS, 0),
    )
    const totalSelloutCX = Number(
      resumo.sellout_total_caixas ??
        selloutPorFamilia.reduce((acc, curr) => acc + curr.caixas, 0),
    )

    const sellout_media_3m_caixas =
      typeof resumo.sellout_media_3m_caixas === 'number'
        ? resumo.sellout_media_3m_caixas
        : undefined

    const sellout_variacao_percentual =
      typeof resumo.sellout_variacao_percentual === 'number'
        ? resumo.sellout_variacao_percentual
        : sellout_media_3m_caixas && sellout_media_3m_caixas > 0
          ? Number(
              (((totalSelloutCX - sellout_media_3m_caixas) / sellout_media_3m_caixas) * 100).toFixed(1),
            )
          : undefined

    const sellout_media_3m_valor =
      typeof resumo.sellout_media_3m_valor === 'number'
        ? resumo.sellout_media_3m_valor
        : typeof resumo.sellout_media_3m_rs === 'number'
          ? resumo.sellout_media_3m_rs
          : undefined

    const sellout_variacao_valor_percentual =
      typeof resumo.sellout_variacao_valor_percentual === 'number'
        ? resumo.sellout_variacao_valor_percentual
        : sellout_media_3m_valor && sellout_media_3m_valor > 0
          ? Number(
              (((totalSelloutRS - sellout_media_3m_valor) / sellout_media_3m_valor) * 100).toFixed(1),
            )
          : undefined

    const totalEstoqueCX = Number(resumo.estoque_total_caixas ?? 0)
    const totalEstoqueRS = Number(resumo.estoque_total_valor ?? 0)
    const cobertura_total_clientes = Number(resumo.cobertura_total_clientes ?? 0)

    return {
      totalSelloutRS,
      totalSelloutCX,
      totalEstoqueCX,
      totalEstoqueRS,
      selloutPorFamilia,
      estoquePorFamilia,
      coberturaPorFamilia,
      resumo: {
        sellout_total_valor: totalSelloutRS,
        sellout_total_caixas: totalSelloutCX,

        // Metas de Sellout
        sellout_meta_caixas: typeof resumo.sellout_meta_caixas === 'number' ? resumo.sellout_meta_caixas : undefined,
        sellout_meta_valor: typeof resumo.sellout_meta_valor === 'number' ? resumo.sellout_meta_valor : undefined,
        sellout_atingimento_caixas_percentual:
          typeof resumo.sellout_atingimento_caixas_percentual === 'number'
            ? resumo.sellout_atingimento_caixas_percentual
            : undefined,
        sellout_atingimento_valor_percentual:
          typeof resumo.sellout_atingimento_valor_percentual === 'number'
            ? resumo.sellout_atingimento_valor_percentual
            : undefined,
        sellout_variacao_meta_caixas:
          typeof resumo.sellout_variacao_meta_caixas === 'number' ? resumo.sellout_variacao_meta_caixas : undefined,
        sellout_variacao_meta_percentual:
          typeof resumo.sellout_variacao_meta_percentual === 'number'
            ? resumo.sellout_variacao_meta_percentual
            : undefined,
        sellout_tendencia_meta: resumo.sellout_tendencia_meta,
        sellout_variacao_meta_valor:
          typeof resumo.sellout_variacao_meta_valor === 'number' ? resumo.sellout_variacao_meta_valor : undefined,
        sellout_variacao_meta_valor_percentual:
          typeof resumo.sellout_variacao_meta_valor_percentual === 'number'
            ? resumo.sellout_variacao_meta_valor_percentual
            : undefined,
        sellout_tendencia_meta_valor: resumo.sellout_tendencia_meta_valor,

        // Médias 3M de Sellout
        sellout_media_3m_caixas,
        sellout_variacao_percentual,
        sellout_tendencia: resumo.sellout_tendencia,
        sellout_media_3m_valor,
        sellout_variacao_valor_percentual,
        sellout_tendencia_valor: resumo.sellout_tendencia_valor,

        estoque_total_caixas: totalEstoqueCX,
        estoque_total_valor: totalEstoqueRS,
        estoque_media_3m_caixas: resumo.estoque_media_3m_caixas,
        estoque_variacao_percentual: resumo.estoque_variacao_percentual,
        estoque_tendencia: resumo.estoque_tendencia,

        cobertura_total_clientes,
        cobertura_potencial_clientes:
          typeof resumo.cobertura_potencial_clientes === 'number'
            ? resumo.cobertura_potencial_clientes
            : undefined,
        cobertura_gap_clientes:
          typeof resumo.cobertura_gap_clientes === 'number'
            ? resumo.cobertura_gap_clientes
            : undefined,
        cobertura_atingimento_potencial_pct:
          typeof resumo.cobertura_atingimento_potencial_pct === 'number'
            ? resumo.cobertura_atingimento_potencial_pct
            : undefined,
        cidades_atuacao_count: resumo.cidades_atuacao_count,
        isYtd: Boolean(resumo.isYtd),

        // Médias 3M de Cobertura
        cobertura_media_3m_clientes: resumo.cobertura_media_3m_clientes,
        cobertura_variacao_percentual: resumo.cobertura_variacao_percentual,
        cobertura_tendencia: resumo.cobertura_tendencia,

        total_familias: Number(resumo.total_familias ?? selloutPorFamilia.length),
      },
      filtrosAplicados,
      mensalDistribuidoresFamilias: Array.isArray(apiData.mensalDistribuidoresFamilias)
        ? apiData.mensalDistribuidoresFamilias.map((m: any) => ({
            dist_id: Number(m.dist_id),
            cod_dist: m.cod_dist ? String(m.cod_dist).trim() : String(m.dist_id),
            dist_nome: String(m.dist_nome || '').trim(),
            familia_cod: String(m.familia_cod || '').trim(),
            familia_nome: String(m.familia_nome || '').trim(),
            mes_num: Number(m.mes_num || 0),
            total_caixas: Number(m.total_caixas || 0),
            total_valor: Number(m.total_valor || 0),
            total_cobertura: Number(m.total_cobertura || 0),
          }))
        : [],
      mesesPeriodo: Array.isArray(apiData.mesesPeriodo)
        ? apiData.mesesPeriodo
        : Array.isArray(filtrosAplicados?.mesesYtd)
          ? filtrosAplicados.mesesYtd
          : [params.mes + 1],
      planoVendaDireta: Array.isArray(apiData.planoVendaDireta)
        ? apiData.planoVendaDireta.map((p: any) => ({
            dist_id: Number(p.dist_id),
            dist_cod: p.dist_cod ? String(p.dist_cod).trim() : String(p.dist_id),
            dist_nome: String(p.dist_nome || '').trim(),
            uf: String(p.uf || '').trim().toUpperCase(),
            gv: '',
            gn: '',
            habitantes: Number(p.habitantes || 0),
            potencial: Number(p.potencial || 0),
            positivacao_total: Number(p.positivacao_total || 0),
            atingimento_pct: Number(p.atingimento_pct || 0),
            macarrao: {
              total: Number(p.macarrao?.total || 0),
              pct_3m: Number(p.macarrao?.pct_3m || 0),
            },
            refresco: {
              total: Number(p.refresco?.total || 0),
              pct_3m: Number(p.refresco?.pct_3m || 0),
            },
            tempero: {
              total: Number(p.tempero?.total || 0),
              pct_3m: Number(p.tempero?.pct_3m || 0),
            },
            salgadinho: {
              total: Number(p.salgadinho?.total || 0),
              pct_3m: Number(p.salgadinho?.pct_3m || 0),
            },
            batata_palha: {
              total: Number(p.batata_palha?.total || 0),
              pct_3m: Number(p.batata_palha?.pct_3m || 0),
            },
            snacks: {
              total: Number(p.snacks?.total || 0),
              pct_3m: Number(p.snacks?.pct_3m || 0),
            },
          }))
        : undefined,
      coberturaAnoCategorias: apiData.coberturaAnoCategorias,
    }
  } catch (err: any) {
    console.error('[ValidacaoSellout] Erro ao buscar dados na API:', err)
    throw err
  }
}


/**
 * 3. Rota: Acompanhamento Diário de Sell-Out
 * POST /sellout/acompanhamento-diario
 */
export const fetchAcompanhamentoDiario = async (
  params: AcompanhamentoDiarioParams,
): Promise<AcompanhamentoDiarioResponse> => {
  try {
    const distIds = (params.distribuidorIds || []).map((id) => {
      const n = Number(id)
      return isNaN(n) ? id : n
    })

    const res = await api.post('/sellout/acompanhamento-diario', {
      distribuidorIds: distIds,
      ano: params.ano,
      mes: params.mes,
    })

    return res.data?.data ?? res.data
  } catch (err: any) {
    console.error('[ValidacaoSellout] Erro ao buscar acompanhamento diário:', err)
    throw err
  }
}
